// ============================================
// Funnel Tracker — Conversion Analytics
//
// Lightweight fire-and-forget event tracking for the
// patient conversion funnel. Writes to Firestore
// `funnel_events` collection. Batches events and
// flushes every 5 seconds or on page unload.
//
// Never blocks UI. Never fails visibly.
// ============================================

import { collection, addDoc, Timestamp, writeBatch, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { C_FUNNEL_EVENTS } from '../../constants/collections';
import type { FunnelEventType, FunnelEvent } from '../../types/eventCatalog';

// Re-export for consumers that import from this file
export type { FunnelEventType, FunnelEvent } from '../../types/eventCatalog';

interface QueuedEvent {
  [key: string]: unknown;
  event: FunnelEventType;
  sessionId: string;
  timestamp: Timestamp;
  page: string;
  referrer: string;
  metadata: Record<string, string | number | boolean>;
}

// ── Session ID — one per browser tab lifecycle ──

let _sessionId: string | null = null;

function getSessionId(): string {
  if (!_sessionId) {
    _sessionId = `s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  }
  return _sessionId;
}

// ── Event queue + flush ──

const COLLECTION_NAME = C_FUNNEL_EVENTS;
const FLUSH_INTERVAL_MS = 5_000;

let _queue: QueuedEvent[] = [];
let _flushTimer: ReturnType<typeof setInterval> | null = null;
let _initialized = false;

function ensureInitialized(): void {
  if (_initialized) return;
  _initialized = true;

  // Periodic flush
  _flushTimer = setInterval(flush, FLUSH_INTERVAL_MS);

  // Flush on page unload (best-effort)
  if (typeof window !== 'undefined') {
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        flush();
      }
    });
    window.addEventListener('pagehide', flush);
  }
}

async function flush(): Promise<void> {
  if (_queue.length === 0) return;

  const batch = [..._queue];
  _queue = [];

  try {
    if (batch.length === 1) {
      // Single write — simpler
      const evt = batch[0];
      await addDoc(collection(db, COLLECTION_NAME), stripUndefined(evt));
    } else {
      // Batch write — up to 500 per batch (Firestore limit)
      const fb = writeBatch(db);
      for (const evt of batch.slice(0, 500)) {
        const ref = doc(collection(db, COLLECTION_NAME));
        fb.set(ref, stripUndefined(evt));
      }
      await fb.commit();

      // If more than 500 (unlikely), re-queue overflow
      if (batch.length > 500) {
        _queue.push(...batch.slice(500));
      }
    }
  } catch (err) {
    // Fire-and-forget — log but never block UI
    console.warn('[FunnelTracker] Flush failed:', err);
    // Re-queue failed events (cap at 100 to prevent memory leak)
    if (_queue.length + batch.length <= 100) {
      _queue.push(...batch);
    }
  }
}

// Firestore rejects `undefined` — strip before writing
function stripUndefined(obj: Record<string, unknown>): Record<string, unknown> {
  const clean: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined) {
      clean[k] = v;
    }
  }
  return clean;
}

// ── Public API ──

/**
 * Track a funnel event. Fire-and-forget.
 * Never throws. Never blocks.
 */
export function trackFunnelEvent(funnelEvent: FunnelEvent): void {
  ensureInitialized();

  const queued: QueuedEvent = {
    event: funnelEvent.event,
    sessionId: getSessionId(),
    timestamp: Timestamp.now(),
    page: typeof window !== 'undefined' ? window.location.pathname : '',
    referrer: typeof document !== 'undefined' ? document.referrer : '',
    metadata: funnelEvent.metadata ?? {},
  };

  _queue.push(queued);
}

/**
 * Force flush — call when you want to ensure events are written
 * (e.g., before navigation). Returns a promise but callers
 * should NOT await this in hot paths.
 */
export function flushFunnelEvents(): Promise<void> {
  return flush();
}

/**
 * Teardown — for testing only.
 */
export function _resetFunnelTracker(): void {
  _queue = [];
  _sessionId = null;
  _initialized = false;
  if (_flushTimer) {
    clearInterval(_flushTimer);
    _flushTimer = null;
  }
}
